import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone, MessageCircle, Instagram, Youtube, ArrowRight } from "lucide-react";
import { Eyebrow, Reveal } from "./Reveal";
import {
    PHONE_DISPLAY,
    PHONE_TEL,
    INSTAGRAM_HANDLE,
    INSTAGRAM_URL,
    YOUTUBE_HANDLE,
    YOUTUBE_URL,
    waLink,
} from "../site";

const INITIAL = {
    name: "",
    phone: "",
    email: "",
    trainingType: "Online Classes",
    experienceLevel: "Beginner",
    goal: "Competition Preparation",
    message: "",
};

const METHODS = [
    {
        icon: Phone,
        label: "Phone",
        value: PHONE_DISPLAY,
        cta: "Call Now",
        href: PHONE_TEL,
        testid: "contact-method-call",
        external: false,
    },
    {
        icon: MessageCircle,
        label: "WhatsApp",
        value: PHONE_DISPLAY,
        cta: "Message on WhatsApp",
        href: waLink("Namaste Trainer Aryan, I would like to know more about your yogasana coaching."),
        testid: "contact-method-whatsapp",
        external: true,
    },
    {
        icon: Instagram,
        label: "Instagram",
        value: INSTAGRAM_HANDLE,
        cta: "Follow Instagram",
        href: INSTAGRAM_URL,
        testid: "contact-method-instagram",
        external: true,
    },
    {
        icon: Youtube,
        label: "YouTube",
        value: YOUTUBE_HANDLE,
        cta: "Subscribe on YouTube",
        href: YOUTUBE_URL,
        testid: "contact-method-youtube",
        external: true,
    },
];

export default function Contact() {
    const [form, setForm] = useState(INITIAL);

    useEffect(() => {
        const handler = (e) =>
            setForm((f) => ({ ...f, trainingType: e.detail }));
        window.addEventListener("prefill-training", handler);
        return () => window.removeEventListener("prefill-training", handler);
    }, []);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const submit = (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.phone.trim()) {
            toast.error("Please add your name and phone number.");
            return;
        }
        const text = [
            "Namaste Trainer Aryan,",
            "",
            "I want to start my training.",
            "",
            `Name: ${form.name}`,
            `Phone: ${form.phone}`,
            `Email: ${form.email || "-"}`,
            `Training Type: ${form.trainingType}`,
            `Experience Level: ${form.experienceLevel}`,
            `Goal: ${form.goal}`,
            "",
            `Message: ${form.message || "-"}`,
        ].join("\n");
        window.open(waLink(text), "_blank");
        toast.success("Opening WhatsApp with your training request.");
    };

    return (
        <section id="contact" data-testid="contact-section" className="relative bg-[#0A0A0A] py-20 sm:py-28 lg:py-32">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_10%,rgba(212,160,23,0.07),transparent_55%)]" />
            <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
                <Eyebrow>Start Today</Eyebrow>
                <Reveal>
                    <h2 className="mt-6 font-display text-5xl tracking-wide sm:text-6xl lg:text-7xl">
                        READY TO <span className="text-gold-grad">TRAIN?</span>
                    </h2>
                    <p className="mt-4 text-[15px] text-[#B8B8B8] sm:text-base">
                        Take your yogasana practice to the next level.
                    </p>
                </Reveal>

                <div className="mt-14 grid gap-12 lg:grid-cols-[0.85fr_1.15fr]">
                    <div className="flex flex-col gap-5">
                        {METHODS.map((m, i) => {
                            const Icon = m.icon;
                            return (
                                <Reveal key={m.label} delay={i * 0.08}>
                                    <a
                                        href={m.href}
                                        target={m.external ? "_blank" : undefined}
                                        rel={m.external ? "noreferrer" : undefined}
                                        data-testid={m.testid}
                                        className="group flex items-center justify-between gap-4 rounded-2xl border border-white/[0.07] bg-white/[0.03] p-6 transition-all duration-400 hover:-translate-y-1 hover:border-[#D4A017]/50 hover:shadow-[0_16px_44px_-18px_rgba(212,160,23,0.35)]"
                                    >
                                        <div className="flex items-center gap-4">
                                            <span className="flex h-12 w-12 items-center justify-center rounded-full border border-[#D4A017]/45 transition-all duration-300 group-hover:shadow-[0_0_20px_rgba(245,197,66,0.35)]">
                                                <Icon size={18} className="text-[#F5C542]" />
                                            </span>
                                            <div>
                                                <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-[#B8B8B8]">
                                                    {m.label}
                                                </p>
                                                <p className="mt-1 font-semibold tracking-wide text-white">
                                                    {m.value}
                                                </p>
                                            </div>
                                        </div>
                                        <span className="hidden text-[10px] font-bold uppercase tracking-[0.2em] text-[#F5C542] opacity-70 transition-opacity group-hover:opacity-100 sm:block">
                                            {m.cta}
                                        </span>
                                    </a>
                                </Reveal>
                            );
                        })}

                        <Reveal delay={0.3}>
                            <div className="glass mt-2 rounded-2xl p-6">
                                <p className="font-display text-xl tracking-[0.14em] text-white">
                                    PREFER A QUICK START?
                                </p>
                                <p className="mt-2 text-xs leading-relaxed text-[#B8B8B8]">
                                    Send the form and your details open directly in
                                    WhatsApp — Trainer Aryan replies personally.
                                </p>
                            </div>
                        </Reveal>
                    </div>

                    <Reveal delay={0.12}>
                        <form
                            data-testid="contact-form"
                            onSubmit={submit}
                            className="glass rounded-3xl p-7 sm:p-9"
                        >
                            <div className="grid gap-5 sm:grid-cols-2">
                                <div>
                                    <label className="field-label" htmlFor="cf-name">Name</label>
                                    <input
                                        id="cf-name"
                                        data-testid="contact-input-name"
                                        className="field"
                                        placeholder="Your full name"
                                        value={form.name}
                                        onChange={set("name")}
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="field-label" htmlFor="cf-phone">Phone Number</label>
                                    <input
                                        id="cf-phone"
                                        data-testid="contact-input-phone"
                                        className="field"
                                        type="tel"
                                        placeholder="+91 ..."
                                        value={form.phone}
                                        onChange={set("phone")}
                                        required
                                    />
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="field-label" htmlFor="cf-email">Email</label>
                                    <input
                                        id="cf-email"
                                        data-testid="contact-input-email"
                                        className="field"
                                        type="email"
                                        placeholder="you@example.com"
                                        value={form.email}
                                        onChange={set("email")}
                                    />
                                </div>
                                <div>
                                    <label className="field-label" htmlFor="cf-type">Training Type</label>
                                    <select
                                        id="cf-type"
                                        data-testid="contact-input-training-type"
                                        className="field appearance-none"
                                        value={form.trainingType}
                                        onChange={set("trainingType")}
                                    >
                                        {["Online Classes", "Offline Classes", "Competition Preparation", "Personal Coaching"].map((o) => (
                                            <option key={o} className="bg-[#111111]">{o}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="field-label" htmlFor="cf-exp">Experience Level</label>
                                    <select
                                        id="cf-exp"
                                        data-testid="contact-input-experience"
                                        className="field appearance-none"
                                        value={form.experienceLevel}
                                        onChange={set("experienceLevel")}
                                    >
                                        {["Beginner", "Intermediate", "Advanced", "Competition Athlete"].map((o) => (
                                            <option key={o} className="bg-[#111111]">{o}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="field-label" htmlFor="cf-goal">Goal</label>
                                    <select
                                        id="cf-goal"
                                        data-testid="contact-input-goal"
                                        className="field appearance-none"
                                        value={form.goal}
                                        onChange={set("goal")}
                                    >
                                        {["Competition Preparation", "Flexibility & Mobility", "Strength & Stamina", "Mental Focus", "Advanced Asana Practice"].map((o) => (
                                            <option key={o} className="bg-[#111111]">{o}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="field-label" htmlFor="cf-message">Message</label>
                                    <textarea
                                        id="cf-message"
                                        data-testid="contact-input-message"
                                        className="field min-h-[110px] resize-y"
                                        placeholder="Tell Trainer Aryan about your practice and goals..."
                                        value={form.message}
                                        onChange={set("message")}
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                data-testid="contact-submit"
                                className="btn-gold mt-7 w-full"
                            >
                                Start My Training <ArrowRight size={15} />
                            </button>
                        </form>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
